import { ApplianceId, Scenario, ScenarioId } from '../../engine/types';

import { byHourSegments } from './curves';

export const deepFreeze: Scenario = {
  description: 'A polar cold snap. Heat is everything, the sun is weak, and the night is very, very long.',
  events: [
    {
      atMinute: 0,
      from: 'Sam',
      id: 'freeze-morning',
      responses: [{ effect: { moodDelta: 2 }, label: 'Drive safe out there' }],
      text: 'Roads are pure ice — taking it slow. It’s -5 windchill. Keep the kids bundled.',
    },
    {
      atMinute: 60,
      from: 'Sam',
      id: 'freeze-warning',
      responses: [
        { effect: { moodDelta: 2 }, label: 'Prepping now.' },
        { effect: { moodDelta: 0, reaction: 'Sam: I’m serious!! blankets staged??' }, label: 'We’ll be fine' },
      ],
      text: 'Utility says the cold snap is overloading the grid. Rolling outage hits us at noon — for real this time.',
    },
    {
      atMinute: 120,
      from: 'Grid Alert',
      id: 'freeze-outage',
      responses: [],
      text: 'The grid buckles under the cold snap. Estimated restoration: noon tomorrow. It’s 16°F and falling.',
    },
    {
      atMinute: 135,
      from: 'Sam',
      id: 'freeze-heat-strategy',
      requiresLoadOff: ApplianceId.HVAC,
      responses: [
        { effect: { moodDelta: 3, startLoads: [ApplianceId.HVAC] }, grantsAsk: true, label: 'Turn the heat on' },
        {
          effect: { moodDelta: -2, reaction: 'Sam: operation blanket fort is GO' },
          label: 'Sweaters and blankets for now',
        },
      ],
      text: 'Power’s out. Turn the heat on, or bundle up and save the battery?',
    },
    {
      atMinute: 135,
      from: 'Sam',
      id: 'freeze-heat-strategy-on',
      requiresLoadOn: ApplianceId.HVAC,
      responses: [
        { effect: { moodDelta: 3, startLoads: [ApplianceId.HVAC] }, grantsAsk: true, label: 'Keep the heat on' },
        {
          effect: {
            moodDelta: -2,
            reaction: 'Sam: operation blanket fort is GO',
            stopLoads: [ApplianceId.HVAC],
          },
          label: 'Sweaters on, heat off for now',
        },
      ],
      text: 'Heat’s still running off the battery. Keep it going, or bundle up and save the juice?',
    },
    {
      atMinute: 330,
      from: 'Home',
      id: 'freeze-kids-home',
      responses: [],
      text: 'School let out early for the cold. Riley and Jojo are home, cheeks bright red.',
    },
    {
      atMinute: 345,
      from: 'Jojo',
      id: 'freeze-cocoa',
      requiresKidsHome: true,
      responses: [
        {
          effect: { durationMinutes: 15, moodDelta: 4, startLoads: [ApplianceId.OVEN] },
          grantsAsk: true,
          label: 'Cocoa time',
        },
        {
          effect: { moodDelta: -1, reaction: 'Jojo: writing this down for my memoirs' },
          label: 'Cocoa at dinner, promise',
        },
      ],
      text: 'can we make hot cocoa 🍫 pleeeease',
    },
    {
      atMinute: 375,
      from: 'Riley',
      id: 'freeze-movie',
      requiresIndoorBelow: 66,
      requiresKidsHome: true,
      responses: [
        {
          effect: { durationMinutes: 120, moodDelta: 4, startLoads: [ApplianceId.TV_CONSOLE] },
          grantsAsk: true,
          label: 'Everyone under the big blanket, movie on',
        },
        {
          effect: { moodDelta: 2, reaction: 'Riley: flashlight shadow puppets it is' },
          label: 'Flashlight reading fort',
        },
      ],
      text: 'we are FROZEN. movie + every blanket we own?',
    },
    {
      atMinute: 375,
      from: 'Riley',
      id: 'freeze-movie-warm',
      requiresIndoorAbove: 66,
      requiresKidsHome: true,
      responses: [
        {
          effect: { durationMinutes: 120, moodDelta: 4, startLoads: [ApplianceId.TV_CONSOLE] },
          grantsAsk: true,
          label: 'Snow day rules. Movie on',
        },
        {
          effect: { moodDelta: 2, reaction: 'Riley: flashlight shadow puppets it is' },
          label: 'Flashlight reading fort',
        },
      ],
      text: 'snow day vibes in here. movie marathon??',
    },
    {
      atMinute: 450,
      from: 'Sam',
      id: 'freeze-dinner',
      responses: [
        {
          effect: { durationMinutes: 60, moodDelta: 5, startLoads: [ApplianceId.OVEN] },
          grantsAsk: true,
          label: 'Soup night',
        },
        {
          effect: { moodDelta: -1, reaction: 'Sam: cold sandwiches in a cold house. bold.' },
          label: 'Sandwiches tonight',
        },
      ],
      text: 'Home! Soup night? Stove would run for an hour or so, but it’d warm everyone up.',
    },
    {
      atMinute: 540,
      from: 'Sam',
      id: 'freeze-pipes',
      responses: [{ effect: { moodDelta: 1 }, label: 'Watching it' }],
      text: 'Freeze warning: if the house drops below 55°F the pipes are at risk. Keep an eye on the thermostat.',
    },
    {
      atMinute: 630,
      from: 'Sam',
      id: 'freeze-ev',
      requiresLoadOff: ApplianceId.EV_CHARGER,
      responses: [
        {
          effect: { durationMinutes: 120, moodDelta: 1, startLoads: [ApplianceId.EV_CHARGER] },
          grantsAsk: true,
          label: 'Top it up for a couple hours',
        },
        { effect: { moodDelta: 0, reaction: 'Sam: living dangerously, I see' }, label: 'It’ll make it' },
      ],
      text: 'Car’s at 30% and the cold eats range. Charge overnight?',
    },
    {
      atMinute: 720,
      from: 'Home',
      id: 'freeze-lights-out',
      responses: [],
      text: 'Lights out. The long, cold night begins.',
    },
    {
      atMinute: 960,
      from: 'Home',
      id: 'freeze-deep-cold',
      responses: [],
      text: '2 AM. The deep freeze peaks. It’s 4°F outside, and the house sheds heat into the dark.',
    },
    {
      atMinute: 1260,
      from: 'Riley',
      id: 'freeze-breakfast',
      requiresKidsHome: true,
      responses: [
        {
          effect: { durationMinutes: 20, moodDelta: 4, startLoads: [ApplianceId.OVEN] },
          grantsAsk: true,
          label: 'Hot oatmeal all around',
        },
        { effect: { moodDelta: -2, reaction: 'Riley: granola bars. in THIS economy.' }, label: 'Granola bars' },
      ],
      text: 'hot oatmeal? please say yes. my toes are theoretical at this point',
    },
    {
      atMinute: 1320,
      from: 'Home',
      id: 'freeze-sun',
      requiresSolar: true,
      responses: [],
      text: 'Cold but bright — low winter sun hits the panels and the house breathes a little easier.',
    },
    {
      atMinute: 1320,
      from: 'Home',
      id: 'freeze-sun-nosolar',
      requiresSolar: false,
      responses: [],
      text: 'Cold but bright — low winter sun pours through the windows and the house warms a touch.',
    },
  ],
  id: ScenarioId.DEEP_FREEZE,
  kidsHomeFromMinute: 330,
  name: 'Deep Freeze',
  outdoorTempByHour: byHourSegments([
    [6, 4],
    [9, 8],
    [12, 14],
    [15, 18],
    [18, 14],
    [21, 9],
    [24, 6],
  ]),
  solarFactorByHour: byHourSegments([[24, 0.7]]),
  solarFactorByHourDayTwo: byHourSegments([[24, 0.7]]),
};
